import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Heart, Clock, Users, Check, Star, ShareNetwork, X, CaretLeft, CaretRight, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl, formatINR } from "@/lib/api";
import { MapView } from "@/components/MapView";
import { Seo, SITE_URL } from "@/components/Seo";
import { ShareMenu } from "@/components/ShareMenu";
import { useSiteInfo, waLink, track } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { StarRating } from "@/components/StarRating";
import { Spinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";

const BOOKING_TYPES = ["Day Out", "Overnight Stay", "Event/Party", "Function"];
const schemaType = { Resorts: "Resort", "Banquet Halls": "EventVenue", "Function Halls": "EventVenue" };

const venueJsonLd = (prop, images) => {
  const url = `${SITE_URL}/property/${prop.id}`;
  const prices = Object.values(prop.prices || {}).filter((v) => Number(v) > 0).map(Number);
  const low = prices.length ? Math.min(...prices) : null;
  const venue = {
    "@type": [schemaType[prop.category] || "LodgingBusiness", "LocalBusiness"], "@id": `${url}#venue`, name: prop.name, url,
    description: prop.description, image: images.filter(Boolean).map(fileUrl),
    address: { "@type": "PostalAddress", streetAddress: prop.address || prop.location, addressLocality: prop.city, addressRegion: prop.state || "Telangana", addressCountry: "IN" },
    ...(prop.latitude && prop.longitude ? { geo: { "@type": "GeoCoordinates", latitude: prop.latitude, longitude: prop.longitude } } : {}),
    ...(low ? { priceRange: `₹${low.toLocaleString("en-IN")}+`, makesOffer: { "@type": "Offer", url, priceCurrency: "INR", price: low, availability: "https://schema.org/InStock", seller: { "@type": "Organization", name: "Avinya Bookings", url: SITE_URL } } } : {}),
    ...(prop.max_guests ? { maximumAttendeeCapacity: prop.max_guests } : {}),
    ...(prop.reviews_count > 0 ? { aggregateRating: { "@type": "AggregateRating", ratingValue: prop.rating, reviewCount: prop.reviews_count, bestRating: 5 } } : {}),
    ...(prop.reviews?.length ? { review: prop.reviews.slice(0, 5).map((r) => ({ "@type": "Review", author: { "@type": "Person", name: r.user_name || "Guest" }, reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 }, reviewBody: r.comment })) } : {}),
  };
  const crumbs = { "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
    { "@type": "ListItem", position: 2, name: prop.category, item: `${SITE_URL}/${prop.category.toLowerCase().replace(/ /g, "-")}/${(prop.location || "").toLowerCase().replace(/ /g, "-")}` },
    { "@type": "ListItem", position: 3, name: prop.name, item: url },
  ] };
  return { "@context": "https://schema.org", "@graph": [venue, crumbs] };
};

const priceKey = { "Day Out": "dayout", "Overnight Stay": "overnight", "Event/Party": "weekend", Function: "weekend" };

export default function PropertyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const info = useSiteInfo();
  const [prop, setProp] = useState(null);
  const [fav, setFav] = useState(false);
  const [bookingType, setBookingType] = useState("Day Out");
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => { setProp(r.data); track("property_viewed", { property_id: id }); }).catch(() => toast.error("Property not found"));
    if (user?.role === "customer") api.get("/favorites/ids").then((r) => setFav(r.data.includes(id))).catch(() => {});
  }, [id, user]);

  const toggleFav = async () => {
    if (!user) return navigate("/login");
    const r = await api.post(`/favorites/${id}`);
    setFav(r.data.favorited);
  };

  if (!prop) return <div className="flex h-screen items-center justify-center"><Spinner /></div>;

  const price = prop.prices[priceKey[bookingType]] || prop.prices.starting;
  const images = prop.images?.length ? prop.images : [""];
  const jsonLd = venueJsonLd(prop, images);

  return (
    <div className="mx-auto max-w-lg pb-28 md:max-w-2xl">
      <Seo
        title={`${prop.name}, ${prop.location}`}
        description={`Book ${prop.name} — a ${prop.category.toLowerCase().replace(/s$/, "")} in ${prop.location}, ${prop.city}. ${(prop.description || "").slice(0, 120)}`}
        path={`/property/${prop.id}`}
        image={fileUrl(images[0])}
        type="article"
        jsonLd={jsonLd}
      />
      <div className="relative">
        <img src={fileUrl(images[activeImg])} alt={prop.name} onClick={() => setLightbox(activeImg)} className="h-72 w-full cursor-zoom-in object-cover" data-testid="gallery-main" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="absolute left-4 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 backdrop-blur">
          <ArrowLeft size={20} />
        </button>
        <div className="absolute right-4 top-5 flex gap-2">
          <ShareMenu property={prop} />
          <button data-testid="detail-favorite" onClick={toggleFav} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 backdrop-blur">
            <Heart size={18} weight={fav ? "fill" : "regular"} className={fav ? "text-red-500" : "text-stone-700"} />
          </button>
        </div>
        {images.length > 1 && (
          <div className="no-scrollbar absolute bottom-3 left-0 flex gap-2 overflow-x-auto px-4">
            {images.map((im, i) => (
              <button key={i} onClick={() => setActiveImg(i)} className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 ${activeImg === i ? "border-white" : "border-transparent"}`}>
                <img src={fileUrl(im)} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 pt-5">
        <span className="rounded-full bg-accent px-3 py-1 text-xs font-medium text-primary">{prop.category}</span>
        <h1 className="mt-3 font-heading text-2xl font-bold text-stone-900">{prop.name}</h1>
        <p className="mt-1 flex items-center gap-1 text-sm text-stone-500"><MapPin size={15} weight="fill" className="text-primary" /> {prop.location}, {prop.city}</p>
        <div className="mt-2 flex items-center gap-3">
          <StarRating rating={prop.rating} size={16} />
          <span className="text-sm text-stone-500">{prop.reviews_count} reviews</span>
          <span className="text-sm text-stone-500">· Up to {prop.max_guests} guests</span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-stone-200 p-3">
            <p className="flex items-center gap-1 text-xs text-stone-500"><Clock size={14} /> Check-in</p>
            <p className="mt-1 font-heading font-semibold">{prop.checkin}</p>
          </div>
          <div className="rounded-xl border border-stone-200 p-3">
            <p className="flex items-center gap-1 text-xs text-stone-500"><Clock size={14} /> Check-out</p>
            <p className="mt-1 font-heading font-semibold">{prop.checkout}</p>
          </div>
        </div>

        <section className="mt-6">
          <h2 className="font-heading text-lg font-semibold">About</h2>
          <p className="mt-2 text-sm leading-relaxed text-stone-600">{prop.description}</p>
        </section>

        <section className="mt-6">
          <h2 className="font-heading text-lg font-semibold">Amenities</h2>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {prop.amenities.map((a) => (
              <div key={a} className="flex items-center gap-2 text-sm text-stone-700">
                <Check size={16} weight="bold" className="text-primary" /> {a}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6">
          <h2 className="font-heading text-lg font-semibold">Property Rules</h2>
          <div className="mt-3 space-y-1 text-sm text-stone-600">
            {Object.entries(prop.rules || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-stone-100 py-1.5">
                <span className="capitalize text-stone-500">{k}</span>
                <span className="font-medium">{v}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6">
          <h2 className="font-heading text-lg font-semibold">Location</h2>
          <p className="mb-3 mt-1 flex items-center gap-1 text-sm text-stone-500"><MapPin size={14} weight="fill" className="text-primary" /> {prop.address ? `${prop.address}, ` : ""}{prop.location}, {prop.city}</p>
          <MapView latitude={prop.latitude} longitude={prop.longitude} address={`${prop.address || ""} ${prop.location} ${prop.city}`} name={prop.name} city={prop.city} />
        </section>

        {prop.reviews?.length > 0 && (
          <section className="mt-6">
            <h2 className="font-heading text-lg font-semibold">Reviews</h2>
            <div className="mt-3 space-y-3">
              {prop.reviews.map((r, i) => (
                <div key={i} className="rounded-xl border border-stone-200 p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-stone-800">{r.user_name}</p>
                    <span className="inline-flex items-center gap-1 text-sm"><Star size={13} weight="fill" className="text-amber-500" /> {r.rating}</span>
                  </div>
                  <p className="mt-1 text-sm text-stone-600">{r.comment}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-6">
          <h2 className="font-heading text-lg font-semibold">Check Availability</h2>
          <p className="mt-1 text-sm text-stone-500">Select a booking type to see pricing.</p>
          <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto">
            {BOOKING_TYPES.map((t) => (
              <button key={t} data-testid={`btype-${t}`} onClick={() => setBookingType(t)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium ${bookingType === t ? "bg-primary text-white" : "bg-stone-100 text-stone-700"}`}>{t}</button>
            ))}
          </div>
          <div className="mt-4 rounded-xl bg-accent/60 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600">{bookingType} price</span>
              <span className="font-heading text-xl font-bold text-primary">{formatINR(price)}</span>
            </div>
          </div>
          {info.support_whatsapp && (
            <a
              data-testid="chat-whatsapp"
              href={waLink(info.support_whatsapp, `Hi Avinya, I'm interested in ${prop.name} (${prop.location}). Can you help me book?`)}
              target="_blank"
              rel="noreferrer"
              onClick={() => track("whatsapp_clicked", { source: "property", property_id: prop.id })}
              className="mt-3 flex items-center justify-center gap-2 rounded-xl border border-green-200 bg-green-50 py-3 text-sm font-semibold text-green-700 hover:bg-green-100"
            >
              <WhatsappLogo size={18} weight="fill" className="text-green-600" /> Chat on WhatsApp
            </a>
          )}
        </section>
      </div>

      <div className="fixed bottom-0 left-1/2 z-40 flex w-full max-w-lg -translate-x-1/2 items-center justify-between border-t border-stone-200 bg-white/90 px-4 pt-3 pb-safe-3 backdrop-blur-xl md:max-w-2xl">
        <div>
          <p className="font-heading text-lg font-bold text-stone-900">{formatINR(price)}</p>
          <p className="text-xs text-stone-500">{bookingType}</p>
        </div>
        <Button
          data-testid="book-now-btn"
          onClick={() => (user ? navigate(`/booking/${id}?type=${encodeURIComponent(bookingType)}`) : navigate("/login"))}
          className="rounded-full bg-primary px-8 py-6 text-base hover:bg-green-900 active:scale-95"
        >
          Book Now
        </Button>
      </div>

      {lightbox !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90" data-testid="lightbox" onClick={() => setLightbox(null)}>
          <button data-testid="lightbox-close" onClick={(e) => { e.stopPropagation(); setLightbox(null); }} className="absolute right-4 top-4 text-white"><X size={30} /></button>
          {images.length > 1 && (
            <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + images.length) % images.length); }} className="absolute left-3 rounded-full bg-white/15 p-2 text-white"><CaretLeft size={28} /></button>
          )}
          <img src={fileUrl(images[lightbox])} alt="" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-[92vw] object-contain" />
          {images.length > 1 && (
            <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % images.length); }} className="absolute right-3 rounded-full bg-white/15 p-2 text-white"><CaretRight size={28} /></button>
          )}
        </div>
      )}
    </div>
  );
}
